// [목적] Day 단위 지도 — 카카오맵 SDK(있으면) 마커·폴리라인·클릭 포커스, 없으면 좌표 정규화 SVG 약도 폴백
// [이유] FlowLine A4(fd5c1e75): planItemSchema.location{lat,lng}|null 실좌표 렌더.
//        VITE_KAKAO_MAP_APP_KEY(또는 VITE_KAKAO_MAP_KEY) 설정 시 카카오맵 JS SDK 를 동적 script 로드(의존성 추가 없음, ponytail).
//        SDK 미설정/로드 실패 시 실좌표를 bounding-box 정규화한 SVG 핀·폴리라인으로 폴백.
//        좌표를 가진 아이템이 하나도 없으면 "위치 정보 없음" 안내(순번 가짜 좌표 생성 금지 — 기존
//        FolioPage.tsx toPoints() 의 index 기반 가짜 분포를 대체).
// [사이드 이팩트] SDK 스크립트는 document.head 에 1회만 삽입(모듈 스코프 Promise 싱글톤) — Day 전환 시
//                재삽입하지 않고 기존 로드된 SDK 를 재사용. 마커 클릭 시 onFocusItem(index) 콜백으로
//                호출부(FolioPage/FolioMobile)가 타임라인 아이템을 하이라이트/스크롤할 수 있게 함.
// [수정 시 고려사항] 확대/축소 버튼은 SDK 모드에서만 렌더(폴백에서는 숨김, Spec 명시).
//                   카카오맵 공식 타입 패키지(@types 등) 미설치 — 이 파일에서 사용하는 최소 API 표면만
//                   로컬 인터페이스로 선언(KakaoMapsNamespace 등). SDK 버전이 바뀌어도 이 표면만 맞으면 동작.

import { useEffect, useRef, useState } from 'react';
import { Box, ButtonBase } from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import type { DayPlan, PlanItem } from '@gangwoncanvas/types';

const c = tokens.color;
const L = 'light' as const;
const INK_HI = c.ink.high[L];
const INK_MID = c.ink.mid[L];
const BORDER = c.line.base[L];
const BRAND = c.brand[500][L];
const R = tokens.radius;
const SH = tokens.shadow;
const SP = tokens.spacing;
const MO = tokens.motion;

// ─── 카카오맵 SDK 최소 타입 선언 (공식 @types 미설치 — 사용 API 표면만) ───────────────

interface KakaoLatLngLike {
  readonly __brand?: 'KakaoLatLng';
}
interface KakaoBoundsLike {
  extend: (pos: KakaoLatLngLike) => void;
}
interface KakaoMapLike {
  setBounds: (bounds: KakaoBoundsLike) => void;
  setLevel: (level: number) => void;
  getLevel: () => number;
  /** [2026-09-21] 타임라인 카드 클릭 → 해당 장소로 지도 이동에 필요 */
  setCenter: (pos: KakaoLatLngLike) => void;
  panTo: (pos: KakaoLatLngLike) => void;
  /** 컨테이너 크기 변경 후 타일/센터를 다시 계산 — 호출하지 않으면 빈 타일이 남는다(QA H-6) */
  relayout: () => void;
}
interface KakaoMarkerLike {
  setMap: (map: KakaoMapLike | null) => void;
}
/**
 * [목적] 번호가 찍힌 커스텀 마커 — kakao.maps.CustomOverlay
 * [이유] [2026-09-21] 기본 Marker 는 동일한 물방울 핀만 그려서 "어디를 몇 번째로 가는지"가 지도에서
 *        전혀 읽히지 않았다(SVG 폴백에는 이미 번호가 있었는데 SDK 모드만 빠져 있던 비대칭).
 *        CustomOverlay 는 임의 DOM 을 얹을 수 있어 방문 순서를 그대로 표기할 수 있다.
 * [수정 시 고려사항] CustomOverlay 는 kakao.maps.event.addListener 의 click 을 지원하지 않는다 —
 *                   content DOM 에 직접 onclick 을 걸어야 한다.
 */
interface KakaoCustomOverlayLike {
  setMap: (map: KakaoMapLike | null) => void;
  setZIndex: (z: number) => void;
}
interface KakaoMapsNamespace {
  LatLng: new (lat: number, lng: number) => KakaoLatLngLike;
  LatLngBounds: new () => KakaoBoundsLike;
  Map: new (
    container: HTMLElement,
    options: { center: KakaoLatLngLike; level: number },
  ) => KakaoMapLike;
  Marker: new (options: { position: KakaoLatLngLike; map?: KakaoMapLike }) => KakaoMarkerLike;
  CustomOverlay: new (options: {
    position: KakaoLatLngLike;
    content: HTMLElement | string;
    map?: KakaoMapLike;
    yAnchor?: number;
    xAnchor?: number;
    zIndex?: number;
  }) => KakaoCustomOverlayLike;
  Polyline: new (options: {
    path: KakaoLatLngLike[];
    strokeWeight: number;
    strokeColor: string;
    strokeOpacity: number;
    map?: KakaoMapLike;
  }) => unknown;
  event: { addListener: (target: unknown, type: string, handler: () => void) => void };
  load: (callback: () => void) => void;
}

declare global {
  interface Window {
    kakao?: { maps: KakaoMapsNamespace };
  }
}

/** 모듈 스코프 싱글톤 — SDK 스크립트 중복 삽입 방지(여러 FolioMap 인스턴스가 동시 마운트돼도 1회만 로드) */
let kakaoLoadPromise: Promise<KakaoMapsNamespace> | null = null;

/**
 * [목적] 카카오맵 JS SDK 동적 로드 — 이미 로드됐으면 즉시 resolve
 * [이유] A4: "VITE_KAKAO_MAP_KEY 가 있으면 카카오맵 JS SDK 를 스크립트 동적 로드(의존성 추가 없이)"
 * [사이드 이팩트] document.head 에 <script> 1개 삽입(최초 1회). 실패 시 kakaoLoadPromise 를 null 로
 *                되돌려 다음 시도(다른 폴리오/새로고침)에서 재시도 가능하게 함.
 * [수정 시 고려사항] appkey 가 잘못된 값이면 SDK 자체는 로드되나 지도 렌더 시 콘솔 에러 — 이 함수는
 *                   script onload/onerror 만 감지(appkey 유효성까지는 검증 못 함, 카카오 SDK 한계).
 */
function loadKakaoMapsSdk(appKey: string): Promise<KakaoMapsNamespace> {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.kakao?.maps) return Promise.resolve(window.kakao.maps);
  if (kakaoLoadPromise) return kakaoLoadPromise;

  kakaoLoadPromise = new Promise<KakaoMapsNamespace>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${encodeURIComponent(appKey)}&autoload=false`;
    script.async = true;
    script.onerror = () => {
      kakaoLoadPromise = null;
      reject(new Error('카카오맵 SDK 로드 실패'));
    };
    script.onload = () => {
      const kakao = window.kakao;
      if (!kakao) {
        kakaoLoadPromise = null;
        reject(new Error('카카오맵 SDK 로드 실패'));
        return;
      }
      kakao.maps.load(() => resolve(kakao.maps));
    };
    document.head.appendChild(script);
  });
  return kakaoLoadPromise;
}

// ─── 좌표 정규화(폴백 SVG용) ──────────────────────────────────────────────────

interface LocatedPoint {
  index: number;
  item: PlanItem;
  lat: number;
  lng: number;
  /** 0~100 퍼센트 좌표(SVG 폴백 전용) */
  x: number;
  y: number;
}

/**
 * [목적] items 중 location 이 있는 항목만 골라 bounding-box 기준 0~100% 좌표로 정규화
 * [이유] A4: "좌표 정규화 SVG 약도 폴백" — 기존 index 기반 가짜 분포(toPoints)를 실좌표 기반으로 대체.
 * [사이드 이팩트] 없음(순수 함수)
 * [수정 시 고려사항] 좌표가 전부 동일(단일 지점)하면 위경도 range=0 → 가운데(50%) 고정.
 *                   여백 10~90% 범위로 정규화해 가장자리 마커가 잘리지 않게 함.
 */
function normalizeLocations(items: PlanItem[]): LocatedPoint[] {
  const located = items
    .map((item, index) => ({ item, index, location: item.location ?? null }))
    .filter(
      (x): x is { item: PlanItem; index: number; location: { lat: number; lng: number } } =>
        x.location != null,
    );
  if (located.length === 0) return [];

  const lats = located.map((x) => x.location.lat);
  const lngs = located.map((x) => x.location.lng);
  const latRange = Math.max(...lats) - Math.min(...lats) || 1;
  const lngRange = Math.max(...lngs) - Math.min(...lngs) || 1;
  const minLat = Math.min(...lats);
  const minLng = Math.min(...lngs);

  return located.map(({ item, index, location }) => ({
    index,
    item,
    lat: location.lat,
    lng: location.lng,
    x: 10 + ((location.lng - minLng) / lngRange) * 80,
    // 위도가 높을수록 지도 위쪽(작은 top%) — 남북 방향 직관 유지
    y: 10 + (1 - (location.lat - minLat) / latRange) * 80,
  }));
}

/**
 * [목적] 지도에서 포커스했을 때 사용할 확대 레벨 — 숫자가 작을수록 확대
 * [이유] [2026-09-21] "장소를 클릭하면 그 지역이 확대되게" 요구. 전체 동선을 보여주는 기본 뷰(setBounds)
 *        에서 한 장소로 파고들 때의 배율이다. 레벨 4 는 대략 동/리 단위가 보이는 배율.
 * [수정 시 고려사항] 너무 낮추면(=과확대) 주변 맥락이 사라져 어디인지 알기 어려워진다.
 */
const FOCUS_LEVEL = 4;

/**
 * [목적] 방문 순서 번호가 찍힌 커스텀 마커 DOM 생성
 * [이유] [2026-09-21] SDK 기본 Marker 는 번호를 표시할 수 없다. SVG 폴백의 원형 번호 핀과 같은 언어로
 *        맞춰 두 모드의 시각이 갈라지지 않게 한다.
 * [사이드 이팩트] 반환 DOM 에 click 핸들러가 붙는다(CustomOverlay 는 SDK 이벤트를 지원하지 않음).
 * [수정 시 고려사항] 활성/비활성 스타일은 applyMarkerStyle 한 곳에서만 바꾼다 — 생성과 갱신이 갈라지면
 *                   포커스 이동 시 이전 마커가 활성 상태로 남는다.
 */
function applyMarkerStyle(el: HTMLElement, active: boolean): void {
  el.style.width = active ? '34px' : '28px';
  el.style.height = active ? '34px' : '28px';
  el.style.fontSize = active ? '14px' : '11.5px';
  el.style.background = active ? BRAND : c.paper[0][L];
  el.style.color = active ? c.paper[0][L] : BRAND;
  el.style.boxShadow = active ? '0 6px 16px rgba(31,111,235,.45)' : '0 2px 6px rgba(20,24,31,.25)';
  el.style.zIndex = active ? '20' : '10';
}

function createNumberedMarkerEl(order: number, label: string, onClick: () => void): HTMLElement {
  const el = document.createElement('button');
  el.type = 'button';
  el.setAttribute('aria-label', `${order}번째 방문지 ${label} — 일정으로 이동`);
  el.textContent = String(order);
  el.style.cssText = [
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'border-radius:50%',
    `border:2px solid ${BRAND}`,
    'font-weight:800',
    "font-family:'JetBrains Mono',ui-monospace,monospace",
    'cursor:pointer',
    'padding:0',
    'transition:all 140ms',
  ].join(';');
  applyMarkerStyle(el, false);
  el.addEventListener('click', (e) => {
    e.stopPropagation();
    onClick();
  });
  return el;
}

// ─── SVG 폴백 ────────────────────────────────────────────────────────────────

function FolioMapSvgFallback({
  points,
  day,
  focusedIndex,
  onFocusItem,
}: {
  points: LocatedPoint[];
  day: DayPlan;
  focusedIndex: number | null;
  onFocusItem?: (index: number) => void;
}): JSX.Element {
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        // 약도 배경 — placeholder 그라디언트, map-integration 폴백 전용 예외
        background: 'linear-gradient(135deg,#E8EFEA,#DCE6E0)',
      }}
    >
      {/* [목적] 폴백 약도의 장소 간 동선 점선
          [이유] 2026-09-18 QA(H-5): `points` 에 `%` 를 넣어 브라우저가 전체 속성을 폐기하고
                 (`<polyline> attribute points: Expected number`) 선이 아예 그려지지 않았다.
                 SVG `points` 는 숫자만 허용하므로, x·y 가 0~100 백분율인 점을 살려
                 viewBox="0 0 100 100" 좌표계로 그린다.
          [사이드 이팩트] preserveAspectRatio="none" 로 컨테이너 비율에 맞춰 늘어난다 —
                          핀(left/top: %) 과 좌표계가 일치해 선과 핀이 항상 겹친다.
                          strokeWidth 도 함께 늘어나므로 vectorEffect 로 굵기를 고정한다.
          [수정 시 고려사항] points 계산(toLocatedPoints)이 % 가 아닌 px 를 반환하도록 바뀌면
                             viewBox 도 같이 바꿀 것 */}
      <Box
        component="svg"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
      >
        <polyline
          points={points.map((p) => `${p.x},${p.y}`).join(' ')}
          fill="none"
          stroke={BRAND}
          strokeWidth="2.5"
          strokeDasharray="6 4"
          vectorEffect="non-scaling-stroke"
          opacity="0.7"
        />
      </Box>
      {points.map((p) => {
        const active = focusedIndex === p.index;
        return (
          <ButtonBase
            key={p.item.id}
            onClick={() => onFocusItem?.(p.index)}
            aria-label={`${p.item.title ?? '이름 없는 장소'} 지도 핀 — 일정으로 이동`}
            sx={{
              position: 'absolute',
              left: `${p.x}%`,
              top: `${p.y}%`,
              transform: 'translate(-50%,-100%)',
              transition: `all ${MO.fast.duration}ms`,
              borderRadius: '50%',
            }}
          >
            <Box
              sx={{
                bgcolor: active ? BRAND : c.paper[0][L],
                color: active ? c.paper[0][L] : BRAND,
                border: `2px solid ${BRAND}`,
                borderRadius: '50%',
                width: active ? 34 : 28,
                height: active ? 34 : 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: active ? 14 : 11,
                fontWeight: 800,
                boxShadow: SH.card,
                fontFamily: 'JetBrains Mono, ui-monospace, monospace',
              }}
            >
              {p.index + 1}
            </Box>
          </ButtonBase>
        );
      })}
      <Box
        sx={{
          position: 'absolute',
          bottom: `${SP[3]}px`,
          left: `${SP[3]}px`,
          bgcolor: `${c.paper[0][L]}F2`,
          borderRadius: `${R.s}px`,
          p: `${SP[2]}px ${SP[3]}px`,
          fontSize: 11,
          color: INK_MID,
          boxShadow: SH.card,
          display: 'flex',
          alignItems: 'center',
          gap: `${SP[2]}px`,
        }}
      >
        <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: BRAND }} />
        {/* [수정: QA-P2-04] points 는 좌표가 있는 항목만 담긴다 — 좌표 없이 직접 추가된 항목은 지도에는 나오지 않았지만
            실제 일정에는 포함되므로, 사용자에게 보여주는 "N개 장소" 카운트는 day.items.length 를 기준으로 해야 한다
            (FolioMobile.tsx 의 기존 관례와 동일). */}
        <Box component="span" sx={{ fontWeight: 600, color: INK_HI, fontSize: 11.5 }}>
          Day {day.dayIndex} · {day.items.length}개 장소
        </Box>
      </Box>
    </Box>
  );
}

// ─── 빈 상태 ─────────────────────────────────────────────────────────────────

// [수정: QA-P2-04] hasItems: 일정에 항목은 있으나 좌표가 있는 항목이 없는 경우 — 이유를 함께 안내한다.
//   (직접 추가한 장소는 좌표가 없어 지도에 표시되지 않는다는 점을 알려 "지도가 고장 났다"는 오해를 막는다.)
function FolioMapEmptyState({ hasItems = false }: { hasItems?: boolean }): JSX.Element {
  return (
    <Box
      role="img"
      aria-label="위치 정보 없음"
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: `${SP[2]}px`,
        bgcolor: c.paper[2][L],
      }}
    >
      <Box aria-hidden="true" sx={{ fontSize: 28 }}>
        📍
      </Box>
      <Box sx={{ fontSize: 12, color: INK_MID, fontWeight: 600 }}>위치 정보 없음</Box>
      {hasItems && (
        <Box sx={{ fontSize: 11, color: INK_MID, textAlign: 'center', px: `${SP[4]}px` }}>
          직접 추가한 장소는 위치 정보가 없어 지도에 표시되지 않아요
        </Box>
      )}
    </Box>
  );
}

// ─── 일부 항목 지도 미표시 안내 ──────────────────────────────────────────────────

/**
 * [목적] 일정에는 있지만 좌표가 없어 지도에 핀이 없는 항목이 있을 때 그 개수를 지도 위에 안내
 * [이유] QA P2-04: 수동 추가 장소(FolioEditPage AddItemForm)는 좌표 없이 저장되어 핀이 생기지 않는데, 하단 카운트는
 *        전체 항목 수("N개 장소")라 핀 개수와 어긋나도 이유를 알 수 없었다. 좌표 부여(지오코딩)는 범위가 커서
 *        하지 않고, 어긋나는 사실과 이유를 알리는 쪽으로 정합성을 맞춘다.
 * [사이드 이팩트] 없음(표시 전용). SDK/폴백 양쪽 모드 공통 오버레이 — 지도 조작(포인터)을 가로채지 않는다.
 * [수정 시 고려사항] 수동 항목에 좌표를 부여하도록 바뀌면(주소 검색 연동 등) 이 안내는 자연히 나타나지 않게 된다.
 */
function UnlocatedNotice({ count }: { count: number }): JSX.Element {
  return (
    <Box
      role="note"
      sx={{
        position: 'absolute',
        top: `${SP[3]}px`,
        left: `${SP[3]}px`,
        zIndex: 2,
        maxWidth: '70%',
        bgcolor: `${c.paper[0][L]}F2`,
        borderRadius: `${R.s}px`,
        p: `${SP[2]}px ${SP[3]}px`,
        fontSize: 11,
        color: INK_MID,
        boxShadow: SH.card,
        pointerEvents: 'none',
      }}
    >
      {count}곳은 위치 정보가 없어 지도에 표시되지 않아요
    </Box>
  );
}

// ─── FolioMap 본체 ───────────────────────────────────────────────────────────

export interface FolioMapProps {
  day: DayPlan;
  /** 현재 하이라이트된 아이템의 day 내 절대 인덱스(호출부 hover/selected 상태) */
  focusedIndex?: number | null;
  /** 마커(SDK)·핀(폴백) 클릭 시 해당 아이템 인덱스 전달 — 호출부가 타임라인 포커스/스크롤 처리 */
  onFocusItem?: (index: number) => void;
}

/**
 * [목적] Day 단위 지도 렌더 — SDK 우선, 실패/미설정 시 SVG 폴백, 좌표 전무 시 빈 상태
 * [이유] A4 요건 전체(SDK 마커·폴리라인·클릭 포커스, 폴백 좌표 정규화, 빈 상태, 확대·축소 SDK 전용).
 * [사이드 이팩트] SDK 모드에서 kakao.maps.Map/Marker/Polyline 인스턴스 생성 — Day 전환 시 이전 마커
 *                cleanup(setMap(null))으로 지도 잔상 방지.
 * [수정 시 고려사항] VITE_KAKAO_MAP_KEY 미설정 시 즉시 폴백(SDK 로드 시도조차 안 함 — 불필요한 네트워크 요청 회피).
 */
export function FolioMap({ day, focusedIndex = null, onFocusItem }: FolioMapProps): JSX.Element {
  const points = normalizeLocations(day.items);
  // [2026-09-18 env 대조] 실제 운영·로컬 env 는 `VITE_KAKAO_MAP_APP_KEY` 이름을 쓰고 있었다(apps/web/.env.local).
  // 코드가 VITE_KAKAO_MAP_KEY 만 읽어 키가 설정돼 있어도 항상 SVG 폴백으로 떨어지던 문제 → 두 이름을 모두 허용하고
  // APP_KEY(실사용 이름)를 우선한다. 하나로 정리할 때는 .env.local·.env.example·Dockerfile ARG 를 함께 변경할 것.
  const kakaoKey =
    (import.meta.env.VITE_KAKAO_MAP_APP_KEY as string | undefined) ||
    (import.meta.env.VITE_KAKAO_MAP_KEY as string | undefined) ||
    '';

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapObjRef = useRef<KakaoMapLike | null>(null);
  // [2026-09-21] 포커스 이동용 — 인덱스별 마커 좌표/DOM 과 전체 동선 bounds
  const markerPosRef = useRef<Map<number, KakaoLatLngLike>>(new Map());
  const markerElRef = useRef<Map<number, HTMLElement>>(new Map());
  const boundsRef = useRef<KakaoBoundsLike | null>(null);
  // [목적] 마커 클릭 핸들러를 항상 최신으로 유지
  // [이유] 마커는 day 변경 시에만 재생성되는데(deps 최소화), onFocusItem 은 부모 렌더마다 새 함수가
  //        온다. 생성 시점 함수를 클로저로 굳히면 이후 갱신된 핸들러가 호출되지 않는다.
  const onFocusItemRef = useRef(onFocusItem);
  onFocusItemRef.current = onFocusItem;
  const [sdkMaps, setSdkMaps] = useState<KakaoMapsNamespace | null>(null);
  const [sdkFailed, setSdkFailed] = useState(false);

  useEffect(() => {
    if (!kakaoKey || points.length === 0) return;
    let cancelled = false;
    loadKakaoMapsSdk(kakaoKey)
      .then((maps) => {
        if (!cancelled) setSdkMaps(maps);
      })
      .catch(() => {
        if (!cancelled) setSdkFailed(true);
      });
    return () => {
      cancelled = true;
    };
    // points.length(0 여부)만 트리거 조건 — 좌표 개수 변화마다 재로드할 필요 없음
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kakaoKey, points.length > 0]);

  const showSdk = Boolean(sdkMaps) && points.length > 0 && !sdkFailed;

  useEffect(() => {
    if (!showSdk || !sdkMaps || !containerRef.current) return;
    const first = points[0];
    if (!first) return;
    const map = new sdkMaps.Map(containerRef.current, {
      center: new sdkMaps.LatLng(first.lat, first.lng),
      level: 8,
    });
    mapObjRef.current = map;
    const bounds = new sdkMaps.LatLngBounds();
    const path: KakaoLatLngLike[] = [];
    const overlays: KakaoCustomOverlayLike[] = [];
    // [2026-09-21] 포커스 이동(타임라인 클릭)에 쓰려고 인덱스별 좌표·DOM 을 보관한다.
    const posByIndex = new Map<number, KakaoLatLngLike>();
    const elByIndex = new Map<number, HTMLElement>();
    for (const p of points) {
      const pos = new sdkMaps.LatLng(p.lat, p.lng);
      bounds.extend(pos);
      path.push(pos);
      const el = createNumberedMarkerEl(p.index + 1, p.item.title ?? '이름 없는 장소', () =>
        onFocusItemRef.current?.(p.index),
      );
      const overlay = new sdkMaps.CustomOverlay({
        position: pos,
        content: el,
        map,
        yAnchor: 0.5,
        xAnchor: 0.5,
        zIndex: 10,
      });
      overlays.push(overlay);
      posByIndex.set(p.index, pos);
      elByIndex.set(p.index, el);
    }
    markerPosRef.current = posByIndex;
    markerElRef.current = elByIndex;
    boundsRef.current = bounds;
    if (path.length > 1) {
      new sdkMaps.Polyline({ path, strokeWeight: 3, strokeColor: BRAND, strokeOpacity: 0.8, map });
    }
    map.setBounds(bounds);

    // [목적] 컨테이너 크기 변경 시 카카오맵 타일 재계산
    // [이유] 2026-09-18 QA(H-6): 카카오 SDK 는 컨테이너 크기 변화를 스스로 감지하지 않는다.
    //         사이드바 접기/펼치기로 지도 폭이 넓어지면 새로 생긴 영역(재현 시 우측 약 35px)이
    //         타일 없는 빈 띠로 남았다. relayout() 후 bounds 를 다시 적용해야 타일과 표시 영역이
    //         함께 복구된다(옵저버 제거 상태에서 재현 확인).
    // [사이드 이팩트] ResizeObserver 1개 추가 — cleanup 에서 disconnect. relayout 은 타일 재요청을
    //                 유발하므로 크기 변화가 있을 때만 호출된다(ResizeObserver 특성).
    // [수정 시 고려사항] relayout 없이 setBounds 만 호출하면 컨테이너 크기는 그대로라 증상이 남는다.
    //                    ResizeObserver 미지원 환경(구형 브라우저)에서는 no-op 로 동작한다.
    const ro =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            map.relayout();
            map.setBounds(bounds);
          });
    ro?.observe(containerRef.current);

    return () => {
      ro?.disconnect();
      overlays.forEach((o) => o.setMap(null));
      markerPosRef.current = new Map();
      markerElRef.current = new Map();
      boundsRef.current = null;
      mapObjRef.current = null;
    };
    // day.id 변경 시(Day 탭 전환) 지도 재생성 — onFocusItem 은 의도적으로 deps 제외(부모 렌더마다 재생성 방지)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSdk, sdkMaps, day.id]);

  // [목적] focusedIndex 변경 → 해당 장소로 지도 이동·확대 + 마커 활성 표시
  // [이유] [2026-09-21 사용자 요청] 타임라인에서 장소를 누르면 "그 지역이 확대"되어야 한다.
  //        기존에는 지도→타임라인 방향(마커 클릭)만 연결돼 있고 반대 방향이 없었다.
  // [사이드 이팩트] panTo 로 부드럽게 이동한다. focusedIndex 가 null 로 돌아오면 전체 동선 뷰(setBounds)
  //                로 복귀해 "어디를 봤는지 잃어버리는" 상황을 막는다.
  // [수정 시 고려사항] 좌표가 없는 항목(location=null)은 markerPosRef 에 없다 — 이때는 지도 이동을
  //                   건너뛰고 아무 것도 하지 않는다(에러 아님).
  useEffect(() => {
    const map = mapObjRef.current;
    if (!showSdk || !map) return;

    // 마커 활성/비활성 스타일 동기화
    markerElRef.current.forEach((el, idx) => applyMarkerStyle(el, idx === focusedIndex));

    if (focusedIndex == null) {
      const b = boundsRef.current;
      if (b) map.setBounds(b);
      return;
    }
    const pos = markerPosRef.current.get(focusedIndex);
    if (!pos) return;
    if (map.getLevel() > FOCUS_LEVEL) map.setLevel(FOCUS_LEVEL);
    map.panTo(pos);
  }, [focusedIndex, showSdk, day.id]);

  function handleZoom(delta: number): void {
    const map = mapObjRef.current;
    if (!map) return;
    map.setLevel(map.getLevel() + delta);
  }

  if (points.length === 0) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: `${R.l}px`,
          overflow: 'hidden',
          border: `1px solid ${BORDER}`,
        }}
      >
        <FolioMapEmptyState hasItems={day.items.length > 0} />
      </Box>
    );
  }

  // [수정: QA-P2-04] 좌표가 없어 핀이 없는 항목 수 — 하단 "N개 장소"(전체 항목 수)와의 차이를 설명한다
  const unlocatedCount = day.items.length - points.length;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        borderRadius: `${R.l}px`,
        overflow: 'hidden',
        border: `1px solid ${BORDER}`,
      }}
    >
      {showSdk ? (
        <Box
          ref={containerRef}
          role="application"
          aria-label={`Day ${day.dayIndex} 지도`}
          sx={{ width: '100%', height: '100%' }}
        />
      ) : (
        <FolioMapSvgFallback
          points={points}
          day={day}
          focusedIndex={focusedIndex}
          onFocusItem={onFocusItem}
        />
      )}
      {unlocatedCount > 0 && <UnlocatedNotice count={unlocatedCount} />}
      {/* 확대/축소 — SDK 모드 전용(Spec: 폴백에서는 숨김) */}
      {showSdk && (
        <Box
          sx={{
            position: 'absolute',
            top: `${SP[3]}px`,
            right: `${SP[3]}px`,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: c.paper[0][L],
            borderRadius: `${R.s}px`,
            boxShadow: SH.card,
            overflow: 'hidden',
          }}
        >
          <ButtonBase
            aria-label="확대"
            onClick={() => handleZoom(-1)}
            sx={{
              width: 32,
              height: 32,
              borderBottom: `1px solid ${BORDER}`,
              fontSize: 14,
              color: INK_HI,
            }}
          >
            +
          </ButtonBase>
          <ButtonBase
            aria-label="축소"
            onClick={() => handleZoom(1)}
            sx={{ width: 32, height: 32, fontSize: 14, color: INK_HI }}
          >
            −
          </ButtonBase>
        </Box>
      )}
    </Box>
  );
}
